import { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'ar';

interface TranslationContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

const translations = {
  en: {
    // Hero
    'hero.badge': 'Electrical Power Engineer',
    'hero.name': 'Reem',
    'hero.lastname': 'Ziadeh',
    'hero.description': 'Graduate of Lattakia University — designing power distribution systems, protection schemes, and electrical installations with precision.',
    'hero.btnWork': 'See My Work',
    'hero.btnAbout': 'About Me',
    'hero.scroll': 'Scroll',
    'hero.stats.exp': 'Years Exp.',
    'hero.stats.projects': 'Projects',
    'hero.stats.graduate': 'Graduate',
    // About
    'about.tag': '01 — About',
    'about.title': 'Engineering with',
    'about.titleHighlight': 'purpose & precision',
    'about.p1': "I'm Reem Ziadeh Born in 1998, an Electrical Power Engineering graduate from Lattakia University (2016–2024), passionate about distribution networks, protection design, and building installations.",
    'about.p2': "I work with DIALux, Revit MEP, ETABS, and AutoCAD — designing complete electrical systems for a four-floor academic institute, including a full lightning protection study per IEC 62305.",
    'about.p3': "In parallel, I have built 8+ years of freelance experience in professional document services — drafting, copying, editing, and formatting reports, contracts, and technical files with strong command of the Microsoft Office suite (Word, Excel, PowerPoint). That work refined my accuracy, speed under deadlines, and clear communication with clients.",
    'about.degree': 'B.Sc. Electrical Power Engineering',
    'about.university': 'Lattakia University · Lattakia, Syria',
    'about.graduated': 'Graduated 2024 ✓',
    // Skills
    'skills.tag': '02 — Skills & Toolkit',
    'skills.title': 'Electrical Power',
    'skills.titleHighlight': 'Engineering',
    'skills.description': 'Specialized expertise in power distribution systems, protection design, and electrical installations acquired through accredited university coursework and hands-on engineering applications.',
    'skills.category1': 'Electrical Engineering',
    'skills.category2': 'Software & Programming',
    'skills.category3': 'Tools & Software',
    'skills.skill1': 'Power Distribution Design',
    'skills.skill2': 'Lightning Protection',
    'skills.skill3': 'Transformer Maintenance',
    'skills.skill4': 'HV / MV / LV Substations',
    'skills.skill5': 'Protection Systems',
    'skills.skill6': 'DIALux',
    'skills.skill7': 'Revit (MEP)',
    'skills.skill8': 'ETABS',
    'skills.skill9': 'AutoCAD (Electrical)',
    'skills.skill10': 'Microsoft Office Suite',
    'skills.skill11': 'Technical Documentation',
    'skills.skill12': 'Project Management',
    // Projects
    'projects.tag': '// projects',
    'projects.title': 'Featured',
    'projects.titleHighlight': 'Work',
    'projects.description': 'Real engineering projects — from design to protection systems — built with industry-standard tools.',
    // Experience
    'experience.tag': '04 — Experience',
    'experience.title': 'Professional',
    'experience.titleHighlight': 'Journey',
    'experience.description': 'A timeline of my engineering education, professional training, and key projects that shaped my expertise in power systems and electrical engineering.',
    // Contact
    'contact.tag': '05 — Contact',
    'contact.title': 'Let\'s build something',
    'contact.titleHighlight': 'powerful together',
    'contact.description': 'Open to opportunities in power engineering, electrical design, and project collaboration.',
    'contact.location': 'Location',
    'contact.phone': 'Phone',
    'contact.formName': 'Name',
    'contact.formEmail': 'Email',
    'contact.formMessage': 'Message',
    'contact.formNamePlaceholder': 'Your name',
    'contact.formEmailPlaceholder': 'your@email.com',
    'contact.formMessagePlaceholder': 'Your message...',
    'contact.sendButton': 'Send Message',
    'contact.sending': 'Sending...',
    'contact.sent': 'Sent Successfully!',
    // Experience Items
    'exp1.period': '2016 — 2024',
    'exp1.title': 'B.Sc. in Electrical Power Engineering',
    'exp1.org': '🎓 Tishreen University, Lattakia',
    'exp1.desc': 'Engineering program covering power systems, protection, distribution networks, electrical machines, and building installations. Graduated with a full-system design project and a Bachelor\'s degree in Electrical Power Engineering.',
    'exp1.skill1': 'Power Systems',
    'exp1.skill2': 'Protection Design',
    'exp1.skill3': 'Distribution Networks',
    'exp1.skill4': 'Electrical Machines',
    'exp2.period': '2016 — Present',
    'exp2.title': 'Freelance Document Services Professional',
    'exp2.org': '💼 Self-Employed, Lattakia',
    'exp2.desc': 'Over 8 years of professional freelance work in printing, scanning, document digitization, writing, and editing — with hands-on daily use of Microsoft Office applications including Word, Excel, and PowerPoint. Built exceptional precision and client relationship skills alongside engineering studies.',
    'exp2.skill1': 'MS Word',
    'exp2.skill2': 'MS Excel',
    'exp2.skill3': 'MS PowerPoint',
    'exp2.skill4': 'Document Management',
    'exp2.skill5': 'Client Relations',
    'exp2.skill6': 'Office Operations',
    'exp3.period': '2025',
    'exp3.title': 'Certified Electrical Maintenance Trainee',
    'exp3.org': '⚡ Lattakia Engineers Syndicate',
    'exp3.desc': 'Hands-on certified training in transformer maintenance, electrical fault diagnosis, and practical field work at switching centers across HV, MV, and LV voltage levels.',
    'exp3.skill1': 'Transformer Maintenance',
    'exp3.skill2': 'Fault Diagnosis',
    'exp3.skill3': 'HV/MV/LV Systems',
    'exp3.skill4': 'Field Training',
    'exp4.period': '2023 — 2024',
    'exp4.title': 'Graduation Project: Institute Power Design',
    'exp4.org': '🏫 Tishreen University Engineering Dept.',
    'exp4.desc': 'Designed complete electrical power supply system for a 4-floor academic institute — including DIALux lighting simulation, load analysis, distribution boards, and a full lightning protection study.',
    'exp4.skill1': 'DIALux',
    'exp4.skill2': 'Load Analysis',
    'exp4.skill3': 'Distribution Boards',
    'exp4.skill4': 'Lightning Protection',
    // Projects Items
    'proj1.title': '4-Floor Academic Institute — Full Electrical Supply',
    'proj1.subtitle': 'Graduation Project · 2024',
    'proj1.overview': 'Designed a complete electrical power distribution system for a four-floor academic institute. The project encompassed everything from load calculations and single-line diagrams to detailed panel board layouts and cable sizing — all compliant with Syrian and international electrical standards.',
    'proj1.highlight1.title': 'Lighting Design',
    'proj1.highlight1.desc': 'Full DIALux simulation across all floors',
    'proj1.highlight2.title': 'Power Distribution',
    'proj1.highlight2.desc': 'Switchboards, feeders & branch circuits',
    'proj1.highlight3.title': 'Technical Drawings',
    'proj1.highlight3.desc': 'AutoCAD & Revit layout plans',
    'proj1.highlight4.title': 'Load Analysis',
    'proj1.highlight4.desc': 'Complete load & demand calculations',
    'proj2.title': 'Substation Training — Engineers Syndicate',
    'proj2.subtitle': 'Certified by Lattakia Engineers Syndicate · 2023–2024',
    'proj2.overview': 'Completed a certified practical training program issued by the Lattakia Engineers Syndicate. The program provided field-level experience in transformer maintenance, electrical fault diagnosis, and hands-on work in real switching centers at HV, MV, and LV levels.',
    'proj2.highlight1.title': 'Transformer Maintenance',
    'proj2.highlight1.desc': 'Oil, insulation & winding inspection',
    'proj2.highlight2.title': 'Fault Detection',
    'proj2.highlight2.desc': 'Diagnosis tools & troubleshooting methods',
    'proj2.highlight3.title': 'HV Switching Centers',
    'proj2.highlight3.desc': 'Live field training at power substations',
    'proj2.highlight4.title': 'Syndicate Certified',
    'proj2.highlight4.desc': 'Official certificate · Engineers Syndicate',
    'proj3.title': 'Freelance Document & Office Services',
    'proj3.subtitle': 'Self-Employed · Multi-Year Experience',
    'proj3.overview': 'Provided professional freelance services for many years in a documents and stationery office, specializing in document photography, scanning, digitization, writing, and editing. Gained extensive hands-on experience with Microsoft Office applications — including Word, Excel, and PowerPoint — while handling a wide range of client documents and administrative paperwork with precision and reliability.',
    'proj3.highlight1.title': 'Document Photography',
    'proj3.highlight1.desc': 'High-quality capture of official papers & files',
    'proj3.highlight2.title': 'Scanning & Digitization',
    'proj3.highlight2.desc': 'Converting physical documents to digital formats',
    'proj3.highlight3.title': 'Writing & Editing',
    'proj3.highlight3.desc': 'Drafting and refining documents for clients',
    'proj3.highlight4.title': 'MS Office Expertise',
    'proj3.highlight4.desc': 'Word, Excel & PowerPoint — years of daily use',
    // Project Modal
    'modal.keyAccomplishments': 'Key Accomplishments',
    'modal.projectToolkit': 'Project Toolkit',
  },
  ar: {
    // Hero
    'hero.badge': 'مهندسة قوى كهربائية',
    'hero.name': 'ريم',
    'hero.lastname': 'زيادة',
    'hero.description': 'خريجة جامعة اللاذقية — تصميم أنظمة توزيع الطاقة، ومخططات الحماية، والتركيبات الكهربائية بدقة.',
    'hero.btnWork': 'شاهد أعمالي',
    'hero.btnAbout': 'عني',
    'hero.scroll': 'مرر',
    'hero.stats.exp': 'سنوات خبرة',
    'hero.stats.projects': 'مشاريع',
    'hero.stats.graduate': 'تخرجت',
    // About
    'about.tag': '٠١ — عني',
    'about.title': 'هندسة بـ',
    'about.titleHighlight': 'هدف ودقة',
    'about.p1': 'أنا ريم زيادة، مواليد ١٩٩٨، خريجة هندسة القوى الكهربائية من جامعة اللاذقية (٢٠١٦–٢٠٢٤)، شغوفة بشبكات التوزيع، تصميم الحماية، والتركيبات المبنية.',
    'about.p2': 'أعمل مع DIALux و Revit MEP و ETABS و AutoCAD — تصميم أنظمة كهربائية كاملة لمعهد أكاديمي مكون من أربعة طوابق، بما في ذلك دراسة حماية كاملة من الصواعق وفقاً لـ IEC 62305.',
    'about.p3': 'بالتوازي، بنيت خبرة ٨+ سنوات في العمل الحر في خدمات المستندات المهنية — صياغة، نسخ، تحرير، وتنسيق التقارير والعقود والملفات التقنية مع إتقان مجموعة Microsoft Office (Word، Excel، PowerPoint). هذا العمل عزز دقتي وسرعتي تحت المواعيد النهائية وتواصلي الواضح مع العملاء.',
    'about.degree': 'بكالوريوس هندسة القوى الكهربائية',
    'about.university': 'جامعة اللاذقية · اللاذقية، سوريا',
    'about.graduated': 'تخرجت ٢٠٢٤ ✓',
    // Skills
    'skills.tag': '٠٢ — المهارات والأدوات',
    'skills.title': 'هندسة القوى',
    'skills.titleHighlight': 'الكهربائية',
    'skills.description': 'خبرة متخصصة في أنظمة توزيع الطاقة، تصميم الحماية، والتركيبات الكهربائية مكتسبة من خلال دورات جامعية معتمدة وتطبيقات هندسية عملية.',
    'skills.category1': 'الهندسة الكهربائية',
    'skills.category2': 'البرمجيات',
    'skills.category3': 'الأدوات والبرمجيات',
    'skills.skill1': 'تصميم أنظمة توزيع الطاقة',
    'skills.skill2': 'حماية الصواعق',
    'skills.skill3': 'صيانة المحولات',
    'skills.skill4': 'محطات فرعية عالية/متوسطة/منخفضة الجهد',
    'skills.skill5': 'أنظمة الحماية',
    'skills.skill6': 'DIALux',
    'skills.skill7': 'Revit (MEP)',
    'skills.skill8': 'ETABS',
    'skills.skill9': 'AutoCAD (كهربائي)',
    'skills.skill10': 'مجموعة Microsoft Office',
    'skills.skill11': 'التوثيق التقني',
    'skills.skill12': 'إدارة المشاريع',
    // Projects
    'projects.tag': '// مشاريع',
    'projects.title': 'أعمال',
    'projects.titleHighlight': 'مميزة',
    'projects.description': 'مشاريع هندسية حقيقية — من التصميم إلى أنظمة الحماية — مبنية بأدوات معيارية في الصناعة.',
    // Experience
    'experience.tag': '٠٤ — الخبرة',
    'experience.title': 'المسيرة',
    'experience.titleHighlight': 'المهنية',
    'experience.description': 'جدول زمني لتعليمي الهندسي، التدريب المهني، والمشاريع الرئيسية التي شكلت خبرتي في أنظمة الطاقة والهندسة الكهربائية.',
    // Contact
    'contact.tag': '٠٥ — تواصل',
    'contact.title': 'لنبنِ شيئاً',
    'contact.titleHighlight': 'قوياً معاً',
    'contact.description': 'مفتوحة للفرص في هندسة القوى، التصميم الكهربائي، والتعاون في المشاريع.',
    'contact.location': 'الموقع',
    'contact.phone': 'الهاتف',
    'contact.formName': 'الاسم',
    'contact.formEmail': 'البريد الإلكتروني',
    'contact.formMessage': 'الرسالة',
    'contact.formNamePlaceholder': 'اسمك',
    'contact.formEmailPlaceholder': 'بريدك@الإلكتروني.com',
    'contact.formMessagePlaceholder': 'رسالتك...',
    'contact.sendButton': 'إرسال الرسالة',
    'contact.sending': 'جاري الإرسال...',
    'contact.sent': 'تم الإرسال بنجاح!',
    // Experience Items
    'exp1.period': '٢٠١٦ — ٢٠٢٤',
    'exp1.title': 'بكالوريوس هندسة القوى الكهربائية',
    'exp1.org': '🎓 جامعة تشرين، اللاذقية',
    'exp1.desc': 'برنامج هندسي يغطي أنظمة الطاقة، الحماية، شبكات التوزيع، الآلات الكهربائية، والتركيبات المبنية. تخرجت بمشروع تصميم نظام كامل وبكالوريوس في هندسة القوى الكهربائية.',
    'exp1.skill1': 'أنظمة الطاقة',
    'exp1.skill2': 'تصميم الحماية',
    'exp1.skill3': 'شبكات التوزيع',
    'exp1.skill4': 'الآلات الكهربائية',
    'exp2.period': '٢٠١٦ — الحاضر',
    'exp2.title': 'محترفة خدمات المستندات الحر',
    'exp2.org': '💼 عمل حر، اللاذقية',
    'exp2.desc': 'أكثر من ٨ سنوات من العمل الحر المهني في الطباعة، المسح الضوئي، رقمية المستندات، الكتابة، والتحرير — مع الاستخدام اليومي العملي لتطبيقات Microsoft Office بما في ذلك Word و Excel و PowerPoint. بنيت مهارات دقة استثنائية وعلاقات العملاء بجانب الدراسات الهندسية.',
    'exp2.skill1': 'MS Word',
    'exp2.skill2': 'MS Excel',
    'exp2.skill3': 'MS PowerPoint',
    'exp2.skill4': 'إدارة المستندات',
    'exp2.skill5': 'علاقات العملاء',
    'exp2.skill6': 'عمليات المكتب',
    'exp3.period': '٢٠٢٥',
    'exp3.title': 'متدربة صيانة كهربائية معتمدة',
    'exp3.org': '⚡ نقابة المهندسين في اللاذقية',
    'exp3.desc': 'تدريب عملي معتمد في صيانة المحولات، تشخيص الأعطال الكهربائية، والعمل الميداني العملي في مراكز التبديل عبر مستويات الجهد العالي والمتوسطة والمنخفض.',
    'exp3.skill1': 'صيانة المحولات',
    'exp3.skill2': 'تشخيص الأعطال',
    'exp3.skill3': 'أنظمة عالية/متوسطة/منخفضة الجهد',
    'exp3.skill4': 'التدريب الميداني',
    'exp4.period': '٢٠٢٣ — ٢٠٢٤',
    'exp4.title': 'مشروع التخرج: تصميم طاقة المعهد',
    'exp4.org': '🏫 قسم الهندسة، جامعة تشرين',
    'exp4.desc': 'تصميم نظام كامل لتزويد الطاقة الكهربائية لمعهد أكاديمي مكون من ٤ طوابق — بما في ذلك محاكاة إضاءة DIALux، تحليل الأحمال، لوحات التوزيع، ودراسة حماية كاملة من الصواعق.',
    'exp4.skill1': 'DIALux',
    'exp4.skill2': 'تحليل الأحمال',
    'exp4.skill3': 'لوحات التوزيع',
    'exp4.skill4': 'حماية الصواعق',
    // Projects Items
    'proj1.title': 'معهد أكاديمي من ٤ طوابق — تزويد كهربائي كامل',
    'proj1.subtitle': 'مشروع تخرج · ٢٠٢٤',
    'proj1.overview': 'تصميم نظام كامل لتوزيع الطاقة الكهربائية لمعهد أكاديمي مكون من ٤ طوابق. شمل المشروع كل شيء من حسابات الأحمال والمخططات أحادية الخط إلى تخطيطات لوحات التبويب التفصيلية وأحجام الكابلات — كلها متوافقة مع المعايير الكهربائية السورية والدولية.',
    'proj1.highlight1.title': 'تصميم الإضاءة',
    'proj1.highlight1.desc': 'محاكاة DIALux كاملة عبر جميع الطوابق',
    'proj1.highlight2.title': 'توزيع الطاقة',
    'proj1.highlight2.desc': 'لوحات التبديل، المغذيات والدوائر الفرعية',
    'proj1.highlight3.title': 'الرسومات التقنية',
    'proj1.highlight3.desc': 'مخططات AutoCAD و Revit',
    'proj1.highlight4.title': 'تحليل الأحمال',
    'proj1.highlight4.desc': 'حسابات كاملة للأحمال والطلب',
    'proj2.title': 'تدريب المحطة الفرعية — نقابة المهندسين',
    'proj2.subtitle': 'معتمد من نقابة المهندسين في اللاذقية · ٢٠٢٣–٢٠٢٤',
    'proj2.overview': 'أكملت برنامج تدريب عملي معتمد صادر عن نقابة المهندسين في اللاذقية. قدم البرنامج خبرة ميدانية في صيانة المحولات، تشخيص الأعطال الكهربائية، والعمل العملي في مراكز التبديل الحقيقية على مستويات الجهد العالي والمتوسطة والمنخفض.',
    'proj2.highlight1.title': 'صيانة المحولات',
    'proj2.highlight1.desc': 'فحص الزيت، العزل والملفات',
    'proj2.highlight2.title': 'كشف الأعطال',
    'proj2.highlight2.desc': 'أدوات التشخيص وطرق استكشاف الأخطاء',
    'proj2.highlight3.title': 'مراكز التبديل عالية الجهد',
    'proj2.highlight3.desc': 'تدريب ميداني حي في محطات الطاقة',
    'proj2.highlight4.title': 'معتمد من النقابة',
    'proj2.highlight4.desc': 'شهادة رسمية · نقابة المهندسين',
    'proj3.title': 'خدمات المستندات والمكتب الحر',
    'proj3.subtitle': 'عمل حر — خبرة متعددة السنوات',
    'proj3.overview': 'قدمت خدمات مهنية حر لسنوات عديدة في مكتب مستندات وأدوات مكتبية، متخصصة في تصوير المستندات، المسح الضوئي، الرقمنة، الكتابة، والتحرير. اكتسبت خبرة عملية واسعة مع تطبيقات Microsoft Office — بما في ذلك Word و Excel و PowerPoint — أثناء التعامل مع مجموعة واسعة من مستندات العملاء والأوراق الإدارية بدقة وموثوقية.',
    'proj3.highlight1.title': 'تصوير المستندات',
    'proj3.highlight1.desc': 'التقاط عالي الجودة للأوراق والملفات الرسمية',
    'proj3.highlight2.title': 'المسح الضوئي والرقمنة',
    'proj3.highlight2.desc': 'تحويل المستندات المادية إلى تنسيقات رقمية',
    'proj3.highlight3.title': 'الكتابة والتحرير',
    'proj3.highlight3.desc': 'صياغة وتحسين المستندات للعملاء',
    'proj3.highlight4.title': 'خبرة MS Office',
    'proj3.highlight4.desc': 'Word و Excel و PowerPoint — سنوات من الاستخدام اليومي',
    // Project Modal
    'modal.keyAccomplishments': 'الإنجازات الرئيسية',
    'modal.projectToolkit': 'أدوات المشروع',
  },
};

export function TranslationProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  const t = (key: string) => {
    return translations[language][key as keyof typeof translations.en] || key;
  };

  return (
    <TranslationContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
}
